// App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import BackgroundVideo from "./components/BackgroundVideo";
import Widgets from "./components/widgets.jsx";

// ✅ moved data into JSON file
import portfolio from "./portfolioText.json";

const PROMPT = "User> ";
const THEME_KEY = "termTheme";

/* ================= THEME BACKGROUND VIDEOS ================= */

// ICE
const ICE_VIDEO_ID = "vz91QpgUjFc";
const ICE_START = 0;
const ICE_OPACITY = 0.18;

// CRT
const CRT_VIDEO_ID = "-Xh4BNbxpI8";
const CRT_START = 272;
const CRT_OPACITY = 0.16;

// GRASS
const GRASS_VIDEO_ID = "WT4M8pGObbw";
const GRASS_START = 204;
const GRASS_OPACITY = 0.15;

// fade timing
const VIDEO_FADE_DELAY_MS = 2000;

function getThemeKey(t) {
  const bg = (t?.bg || "").toLowerCase();
  const fg = (t?.fg || "").toLowerCase();
  if (bg === "#030506" && fg === "#b8ffbf") return "CRT";
  if (bg === "#0b1020" && fg === "#7bdcff") return "ICE";
  if (bg === "#0f6f2f" && fg === "#ffff03") return "GRASS";
  return "CUSTOM";
}

function videoForTheme(themeKey) {
  if (themeKey === "ICE")
    return { id: ICE_VIDEO_ID, start: ICE_START, opacity: ICE_OPACITY };
  if (themeKey === "CRT")
    return { id: CRT_VIDEO_ID, start: CRT_START, opacity: CRT_OPACITY };
  if (themeKey === "GRASS")
    return { id: GRASS_VIDEO_ID, start: GRASS_START, opacity: GRASS_OPACITY };
  return null;
}

/* ================= PORTFOLIO DATA (FROM JSON) ================= */

const PROJECTS = portfolio.projects || [];
const ABOUT_LINES = portfolio.aboutLines || [];
const SKILLS_SECTIONS = portfolio.skillsSections || [];
const EXPERIENCE = portfolio.experience || [];
const EDUCATION_LINES = portfolio.educationLines || [];
const CONTACT_LINES = portfolio.contactLines || [];

/* ================= HELPERS ================= */

function wrapLines(text, width = 78) {
  const words = text.split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > width) {
      if (cur) lines.push(cur);
      cur = w;
    } else {
      cur = (cur ? cur + " " : "") + w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function nowStamp() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

function calcWindowWidth() {
  const vw = window.innerWidth;
  const max = 1100;
  const available = Math.max(280, vw - 48);
  return Math.min(max, available);
}

export default function App() {
  const [vw, setVw] = useState(() => window.innerWidth);
  const isSmall = vw < 720;

  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener("resize", onR);
    window.addEventListener("orientationchange", onR);
    return () => {
      window.removeEventListener("resize", onR);
      window.removeEventListener("orientationchange", onR);
    };
  }, []);

  const [lines, setLines] = useState(() => [
    `(${nowStamp()}) *** TERMLINK PROTOCOL ***`,
    `(${nowStamp()}) ENTER COMMAND  |  TYPE 'HELP' FOR ASSISTANCE`,
    "",
    "WELCOME, OPERATOR.",
    "THIS TERMINAL CONTAINS PERSONAL PORTFOLIO RECORDS.",
    "",
    "AVAILABLE: HELP  ABOUT  PROJECTS  SKILLS  EXPERIENCE  EDUCATION  CONTACT  CLEAR",
    "",
  ]);

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIndex, setHistIndex] = useState(-1);
  const [caretPos, setCaretPos] = useState(0);
  const wrapWidth = 78;

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  /* ================= THEME STATE ================= */

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved ? JSON.parse(saved) : { bg: "#030506", fg: "#b8ffbf" };
  });

  const themeKey = useMemo(() => getThemeKey(theme), [theme]);
  const bgVideo = useMemo(() => videoForTheme(themeKey), [themeKey]);

  const [themeOpen, setThemeOpen] = useState(false);
  const themeBtnRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--term-bg", theme.bg);
    root.style.setProperty("--term-fg", theme.fg);
    localStorage.setItem(THEME_KEY, JSON.stringify(theme));
  }, [theme]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setThemeOpen(false);
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, []);

  /* ================= DRAGGABLE + RESIZABLE TERMINAL WINDOW ================= */

  const termWindowRef = useRef(null);
  const dragRef = useRef({
    dragging: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
  });

  const resizeRef = useRef({
    resizing: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startW: 0,
    startH: 0,
  });

  const defaultWinH = () =>
    Math.min(820, Math.round(window.innerHeight * 0.74));

  const [winW, setWinW] = useState(() => calcWindowWidth());
  const [winH, setWinH] = useState(() => defaultWinH());
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [posReady, setPosReady] = useState(false);

  const clampToViewport = (left, top, width, height) => {
    const margin = 12;
    const maxLeft = Math.max(margin, window.innerWidth - width - margin);
    const maxTop = Math.max(margin, window.innerHeight - height - margin);

    return {
      left: Math.min(Math.max(margin, left), maxLeft),
      top: Math.min(Math.max(margin, top), maxTop),
    };
  };

  const centerWindow = (width, height) => {
    const margin = 12;
    const left = Math.max(margin, Math.round((window.innerWidth - width) / 2));
    const top = Math.max(margin, Math.round((window.innerHeight - height) / 2));
    return { left, top };
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const width = calcWindowWidth();
      const height = defaultWinH();
      setWinW(width);
      setWinH(height);
      setPos(centerWindow(width, height));
      setPosReady(true);
    }, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 720) {
        setWinW(Math.max(280, window.innerWidth - 24));
        setWinH(Math.max(320, Math.round(window.innerHeight * 0.78)));
        setPos({ left: 12, top: 12 });
        setPosReady(true);
        return;
      }

      const width = calcWindowWidth();
      setWinW(width);

      const maxH = Math.min(1000, window.innerHeight - 24);
      const nextH = Math.max(420, Math.min(maxH, winH));
      setWinH(nextH);

      setPos((p) => {
        const next = posReady
          ? clampToViewport(p.left, p.top, width, nextH)
          : centerWindow(width, nextH);
        return next;
      });
      setPosReady(true);
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [posReady, winH]);

  const onDragStart = (e) => {
    if (isSmall) return;
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();

    dragRef.current.dragging = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.startLeft = pos.left;
    dragRef.current.startTop = pos.top;

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const onDragMove = (e) => {
    if (isSmall) return;
    if (!dragRef.current.dragging) return;
    if (dragRef.current.pointerId !== e.pointerId) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    setPos(
      clampToViewport(
        dragRef.current.startLeft + dx,
        dragRef.current.startTop + dy,
        winW,
        winH,
      ),
    );
    setPosReady(true);
  };

  const onDragEnd = (e) => {
    if (dragRef.current.pointerId !== e.pointerId) return;
    dragRef.current.dragging = false;
    dragRef.current.pointerId = null;
  };

  const isResizableViewport = () => !isSmall && window.innerWidth >= 768;

  const onResizeStart = (e) => {
    if (!isResizableViewport()) return;
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    resizeRef.current.resizing = true;
    resizeRef.current.pointerId = e.pointerId;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startW = winW;
    resizeRef.current.startH = winH;

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const onResizeMove = (e) => {
    if (!resizeRef.current.resizing) return;
    if (resizeRef.current.pointerId !== e.pointerId) return;

    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;

    const minW = 520;
    const minH = 420;
    const maxW = Math.min(1400, window.innerWidth - 24);
    const maxH = Math.min(1000, window.innerHeight - 24);

    const nextW = Math.max(minW, Math.min(maxW, resizeRef.current.startW + dx));
    const nextH = Math.max(minH, Math.min(maxH, resizeRef.current.startH + dy));

    setWinW(nextW);
    setWinH(nextH);
    setPos((p) => clampToViewport(p.left, p.top, nextW, nextH));
    setPosReady(true);
  };

  const onResizeEnd = (e) => {
    if (resizeRef.current.pointerId !== e.pointerId) return;
    resizeRef.current.resizing = false;
    resizeRef.current.pointerId = null;
  };

  /* ================= TERMINAL LOGIC ================= */

  const commands = useMemo(
    () => [
      "HELP",
      "ABOUT",
      "PROJECTS",
      "OPEN",
      "SKILLS",
      "EXPERIENCE",
      "EDUCATION",
      "CONTACT",
      "CLEAR",
    ],
    [],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [lines, busy]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const posSel = Math.max(0, Math.min(caretPos, input.length));
    try {
      el.setSelectionRange(posSel, posSel);
    } catch {}
  }, [caretPos, input]);

  const print = (...out) => setLines((prev) => [...prev, ...out]);

  const withDelay = async (fn) => {
    setBusy(true);
    await new Promise((r) => setTimeout(r, 180));
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const runCommand = async (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    print(`${PROMPT}${trimmed}`);

    setCmdHistory((prev) => [...prev, trimmed].slice(-50));
    setHistIndex(-1);

    const [head, ...rest] = trimmed.split(/\s+/);
    const cmd = head.toUpperCase();
    const arg = rest.join(" ").trim();

    if (cmd === "CLEAR") {
      setLines([
        `(${nowStamp()}) *** User INDUSTRIES (TM) TERMLINK PROTOCOL ***`,
        `(${nowStamp()}) ENTER COMMAND  |  TYPE 'HELP' FOR ASSISTANCE`,
        "",
      ]);
      return;
    }

    if (cmd === "HELP") {
      await withDelay(async () =>
        print("", "COMMAND LIST:", ...commands.map((c) => `  ${c}`), ""),
      );
      return;
    }

    if (cmd === "ABOUT") {
      await withDelay(async () => {
        print("", ...ABOUT_LINES, "");
      });
      return;
    }

    if (cmd === "SKILLS") {
      await withDelay(async () => {
        print("", "SKILLS", "--------------------------------");
        SKILLS_SECTIONS.forEach((sec) => {
          print(`${sec.title}:`);
          (sec.items || []).forEach((it) => print(`  - ${it}`));
          print("");
        });
      });
      return;
    }

    if (cmd === "EXPERIENCE") {
      await withDelay(async () => {
        print("", "EXPERIENCE LOG", "--------------------------------");
        EXPERIENCE.forEach((job) => {
          print(`${job.role} — ${job.org} (${job.dates})`);
          (job.bullets || []).forEach((b) => print(`  • ${b}`));
          print("");
        });
      });
      return;
    }

    if (cmd === "EDUCATION") {
      await withDelay(async () => {
        print("", ...EDUCATION_LINES, "");
      });
      return;
    }

    if (cmd === "CONTACT") {
      await withDelay(async () => {
        print("", ...CONTACT_LINES, "");
      });
      return;
    }

    if (cmd === "PROJECTS") {
      await withDelay(async () => {
        print("", "PROJECT FILES:");
        PROJECTS.forEach((p) => print(`  - ${p.name}`));
        print("", "USE: OPEN <NAME>", "");
      });
      return;
    }

    if (cmd === "OPEN") {
      await withDelay(async () => {
        const key = arg.trim().toLowerCase();

        const p = PROJECTS.find(
          (x) => (x.name || "").trim().toLowerCase() === key,
        );

        if (!p)
          return print("", `ERROR: FILE NOT FOUND: ${arg.toUpperCase()}`, "");

        print(
          "",
          `Project: ${p.name}`,
          "--------------------------------",
          ...wrapLines(p.desc, wrapWidth),
          "",
          // `LINK: ${p.link}`,
          "",
        );
      });
      return;
    }
    await withDelay(async () => print("", `UNKNOWN COMMAND: ${cmd}`, ""));
  };

  const autocomplete = () => {
    const first = input.trimStart().split(/\s+/)[0]?.toUpperCase();
    const matches = commands.filter((c) => c.startsWith(first));
    if (matches.length === 1) {
      setInput(matches[0]);
      setCaretPos(matches[0].length);
    } else if (matches.length > 1) {
      print("", "MATCHES:", ...matches.map((m) => `  ${m}`), "");
    }
  };

  const applyHistory = (nextIndex) => {
    if (cmdHistory.length === 0) return;

    if (nextIndex < 0) {
      setHistIndex(-1);
      setInput("");
      setCaretPos(0);
      return;
    }

    const clamped = Math.min(nextIndex, cmdHistory.length - 1);
    const cmd = cmdHistory[cmdHistory.length - 1 - clamped] || "";
    setHistIndex(clamped);
    setInput(cmd);
    setCaretPos(cmd.length);
  };

  const onKeyDown = (e) => {
    if (busy && e.key === "Enter") return e.preventDefault();

    if (e.key === "Enter") {
      e.preventDefault();
      const toRun = input;
      setInput("");
      setCaretPos(0);
      return runCommand(toRun);
    }

    if (e.key === "Tab") {
      e.preventDefault();
      return autocomplete();
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      return applyHistory(histIndex + 1);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      return applyHistory(histIndex - 1);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      return setCaretPos((p) => Math.max(0, p - 1));
    }

    if (e.key === "ArrowRight") {
      e.preventDefault();
      return setCaretPos((p) => Math.min(input.length, p + 1));
    }

    if (e.key === "Home") {
      e.preventDefault();
      return setCaretPos(0);
    }

    if (e.key === "End") {
      e.preventDefault();
      return setCaretPos(input.length);
    }
  };

  const [widgetsMenuOpen, setWidgetsMenuOpen] = useState(false);
  const widgetsAnchorRef = useRef(null);

  return (
    <div className="crt">
      <style>{`
        /* Page scroll container */
        .app-page{
          min-height: 100vh;
          overflow-x: hidden;
        }

        /* Terminal window (fixed) */
        .term-window{ position: ${isSmall ? "relative" : "fixed"}; z-index: 2; }
        .term-window .terminal{ height: 100%; display:flex; flex-direction:column; min-height:0; }
        .term-window .terminal-inner{ flex:1 1 auto; min-height:0; overflow:auto; -webkit-overflow-scrolling:touch; }

        /* On small screens terminal becomes part of normal flow */
        @media (max-width: 720px){
          .term-window{
            width: calc(100vw - 24px) !important;
            height: auto !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
            margin: 12px;
          }
          .term-window .terminal{
            height: min(70vh, 720px);
          }
        }

        .term-titlebar{
          user-select:none;
          -webkit-user-select:none;
          touch-action:none;
          cursor: ${isSmall ? "default" : "grab"};
          padding: 10px 14px;
          border: 2px solid var(--term-frame);
          border-bottom: 0;
          background: rgba(0,0,0,0.55);
          display:flex;
          align-items:center;
          gap:10px;
          letter-spacing: 0.08em;
          font-size: 12px;
          opacity: 0.92;
        }
        .term-titlebar:active{ cursor: ${isSmall ? "default" : "grabbing"}; }
        .term-window .terminal { border-top: 0; }

        .resize-grip{
          padding: 2px 8px;
          border: 1px solid var(--term-frame-soft);
          background: rgba(0,0,0,0.35);
          cursor: nwse-resize;
          user-select:none;
          touch-action:none;
        }

        /* Dock area where small-screen widgets stack */
        .dock{
          width: calc(100vw - 24px);
          margin: 0 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        @media (min-width: 721px){
          .dock{ display:none; }
        }
      `}</style>

      <BackgroundVideo
        video={bgVideo}
        visibleDelayMs={VIDEO_FADE_DELAY_MS}
        title={`${themeKey} background video`}
      />

      <div className="app-page">
        <div
          ref={termWindowRef}
          className="term-window"
          style={
            isSmall
              ? {}
              : {
                  width: `${winW}px`,
                  height: `${winH}px`,
                  left: posReady ? `${pos.left}px` : "50%",
                  top: posReady ? `${pos.top}px` : "50%",
                  transform: posReady ? "none" : "translate(-50%, -50%)",
                }
          }
          onClick={() => inputRef.current?.focus()}
        >
          <div
            className="term-titlebar"
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          >
            <span style={{ marginLeft: 6 }}>
              TERMINAL — {themeKey === "CUSTOM" ? "CUSTOM" : themeKey}
            </span>

            {!isSmall && (
              <span
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <span style={{ opacity: 0.8 }}>drag</span>

                <span
                  className="resize-grip"
                  onPointerDown={onResizeStart}
                  onPointerMove={onResizeMove}
                  onPointerUp={onResizeEnd}
                  onPointerCancel={onResizeEnd}
                  title="Resize"
                >
                  resize
                </span>
              </span>
            )}
          </div>

          <div className="terminal">
            <div className="terminal-inner" ref={containerRef}>
              {lines.map((l, i) => (
                <div key={i} className="line">
                  {l || "\u00A0"}
                </div>
              ))}
              {busy && <div className="line dim">(processing...)</div>}

              <div className="input-row">
                <span className="prompt">{PROMPT}</span>

                <div className="line-render">
                  <span>{input.slice(0, caretPos)}</span>
                  <span className="block-cursor">
                    {input[caretPos] || "\u00A0"}
                  </span>
                  <span>{input.slice(caretPos + 1)}</span>
                </div>

                <input
                  ref={inputRef}
                  className="input-capture"
                  value={input}
                  onChange={(e) => {
                    const next = e.target.value;
                    setInput(next);
                    const posSel =
                      typeof e.target.selectionStart === "number"
                        ? e.target.selectionStart
                        : next.length;
                    setCaretPos(posSel);
                  }}
                  onKeyDown={onKeyDown}
                />
              </div>
            </div>

            <div className="hintbar">
              <span className="hint">TAB: autocomplete</span>
              <span className="hint">↑/↓: history</span>
              <span className="hint">HELP: commands</span>
              <div className="hint-spacer" />

              <button
                ref={themeBtnRef}
                className="hint-btn"
                onClick={() => setThemeOpen((v) => !v)}
              >
                THEME
              </button>

              <button
                ref={widgetsAnchorRef}
                className="hint-btn"
                onClick={() => setWidgetsMenuOpen((v) => !v)}
              >
                WIDGETS
              </button>

              {themeOpen && (
                <div id="theme-popover" className="popover">
                  <div className="popover-title">TERMINAL THEME</div>

                  <label className="row">
                    <span>Background</span>
                    <input
                      type="color"
                      value={theme.bg}
                      onChange={(e) =>
                        setTheme((t) => ({ ...t, bg: e.target.value }))
                      }
                    />
                  </label>

                  <label className="row">
                    <span>Text</span>
                    <input
                      type="color"
                      value={theme.fg}
                      onChange={(e) =>
                        setTheme((t) => ({ ...t, fg: e.target.value }))
                      }
                    />
                  </label>

                  <div className="preset-row theme-crt">
                    <button
                      className="preset"
                      onClick={() => setTheme({ bg: "#030506", fg: "#b8ffbf" })}
                    >
                      CRT
                    </button>
                    <button
                      className="preset theme-ice"
                      onClick={() => setTheme({ bg: "#0b1020", fg: "#7bdcff" })}
                    >
                      ICE
                    </button>
                    <button
                      className="preset theme-grass"
                      onClick={() => setTheme({ bg: "#0f6f2f", fg: "#FFFF03" })}
                    >
                      GRASS
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dock (small screens): widgets stack here and page scrolls to them */}
        <div className="dock" id="dock">
          <Widgets
            open={widgetsMenuOpen}
            onClose={() => setWidgetsMenuOpen(false)}
            anchorEl={widgetsAnchorRef.current}
            docked={true}
          />
        </div>

        {/* Desktop/tablet: floating windows */}
        {!isSmall && (
          <Widgets
            open={widgetsMenuOpen}
            onClose={() => setWidgetsMenuOpen(false)}
            anchorEl={widgetsAnchorRef.current}
            docked={false}
          />
        )}
      </div>
    </div>
  );
}
